import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StickyNote } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '@/shared/api';
import { PanelCard } from './PanelCard';
import { ProvenanceTag } from './ProvenanceTag';

// Freie Team-Notiz am Thread — festhalten, worauf man beim naechsten Kontakt achten soll.
// Speichert on-blur (nur bei Aenderung). Im Demo read-only.
export function NoteCard({
  threadId,
  initialNote,
  readOnly,
}: {
  threadId: string;
  initialNote: string | null;
  readOnly?: boolean;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [note, setNote] = useState(initialNote ?? '');
  const [saved, setSaved] = useState(initialNote ?? '');

  const save = useMutation({
    mutationFn: (text: string) => api.messaging.setNote(threadId, text || null),
    // saved erst NACH Erfolg fortschreiben — bei Fehler bleibt die Baseline alt, ein Re-Blur speichert erneut.
    onSuccess: (_data, text) => {
      setSaved(text);
      void qc.invalidateQueries({ queryKey: ['inbox', 'thread', threadId] });
    },
    onError: () => toast.error('Notiz konnte nicht gespeichert werden.'),
  });

  function handleBlur() {
    if (readOnly) return;
    const value = note.trim();
    if (value === saved) return;
    save.mutate(value);
  }

  return (
    <PanelCard
      icon={StickyNote}
      title="Interne Notiz"
      hint={saved ? saved : 'keine'}
      trailing={<ProvenanceTag source="internal" />}
    >
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={handleBlur}
        readOnly={readOnly}
        placeholder={readOnly ? '—' : 'Interne Notiz zum Gast/Thread …'}
        rows={3}
        className="border-line bg-card-alt text-ink-soft focus:border-ink-faint w-full resize-none rounded-md border px-3 py-2 text-[12.5px] outline-none"
      />
      {save.isPending && <p className="text-ink-faint mt-1 text-[11px]">Speichere …</p>}
      {save.isSuccess && !save.isPending && note.trim() === saved && (
        <p className="text-sage mt-1 text-[11px]">Gespeichert</p>
      )}
    </PanelCard>
  );
}
